import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Memberships", href: "#memberships" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleAnchor = (e, href) => {
    if (!isHome) return; // navigate normally via Link
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/80 border-b border-white/5"
          : "backdrop-blur-md bg-[#050505]/40 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_elite-training-hub-29/artifacts/pe71jees_5CB65146-0216-4877-B45D-E702230BD224.png"
              alt="BlueprintHoopz"
              className="h-10 md:h-12 w-auto"
            />
            <span className="hidden sm:inline font-display text-xl md:text-2xl font-bold tracking-tight uppercase">
              Blueprint<span className="text-[#2563EB]">Hoopz</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={isHome ? link.href : `/${link.href}`}
                onClick={(e) => handleAnchor(e, link.href)}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="text-sm font-medium text-white/80 hover:text-white tracking-wider uppercase transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/my-pack"
              data-testid="nav-mypack-link"
              className="hidden md:inline-flex items-center text-xs font-medium tracking-widest uppercase text-white/70 hover:text-[#60A5FA] transition-colors mr-2"
            >
              Customer Portal
            </Link>
            <Link
              to="/book"
              data-testid="nav-book-now-btn"
              className="hidden sm:inline-flex items-center bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs md:text-sm font-bold tracking-widest uppercase px-5 md:px-7 py-3 transition-colors rounded-sm"
            >
              Book Now
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              className="lg:hidden text-white p-2"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden pb-6 pt-2 border-t border-white/5">
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={isHome ? link.href : `/${link.href}`}
                  onClick={(e) => handleAnchor(e, link.href)}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  className="py-3 text-base font-medium tracking-wide text-white/85 hover:text-[#60A5FA]"
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/my-pack"
                data-testid="nav-mobile-mypack-link"
                className="mt-3 text-base font-medium tracking-wide text-white/85 hover:text-[#60A5FA] py-2"
              >
                Customer Portal
              </Link>
              <Link
                to="/book"
                data-testid="nav-mobile-book-btn"
                className="mt-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold tracking-widest uppercase px-6 py-3 text-center rounded-sm"
              >
                Book Now
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
